import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import {
  getInboxWorkflows,
  type InboxListAction,
  type InboxListPagination,
  type InboxListWorkflow,
  type InboxWorkflowsPage,
  type InboxZoneOption,
} from 'api/workflows/endpoints/getInboxWorkflows';
import { putWorkflowAction, type SaveWorkflowAction, type UpdatedWorkflowAction } from 'api/workflows/endpoints/putWorkflowAction';
import { putWorkflowRead } from 'api/workflows/endpoints/putWorkflowRead';
import { putWorkflowRespond, type RespondedWorkflowAction } from 'api/workflows/endpoints/putWorkflowRespond';
import { acceptDegraded, type Received } from 'validation';
import { toDraft } from './toDraft';

interface WorkflowState {
  workflows: InboxListWorkflow[];
  filterOptions: readonly string[];
  zoneOptions: readonly InboxZoneOption[];
  pagination: InboxListPagination | null;
  /** The list read whose answer the list waits for; an older one that answers later is ignored. */
  listRequestId: string | null;
  loading: boolean;
  createLoading: boolean;
  updateLoading: boolean;
  deleteLoading: boolean;
  error: string | null;
}

// Initial state for workflows slice
const initialState: WorkflowState = {
  workflows: [],
  filterOptions: [],
  zoneOptions: [],
  pagination: null,
  listRequestId: null,
  loading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  error: null,
};

// Thunks for asynchronous operations

/** What a write of an action answers with, as the list keeps it. */
type WrittenWorkflowAction = RespondedWorkflowAction | UpdatedWorkflowAction;

/** The list row's view of a written action: whether it is still open is all the sidebar reads. */
const listActionOf = (written: WrittenWorkflowAction): InboxListAction => ({
  workflow_action_id: written.workflow_action_id,
  response_options: written.response_options,
  response: written.response,
});

/**
 * A write's receipt, or null when the write landed but its receipt could not be read: that is not a failed write
 * (#1035), so the thunk fulfils and the list keeps what it had until its next read.
 */
const receiptOf = <T>(answer: Received<T>): T | null => (answer.result === 'unreadable' ? null : acceptDegraded(answer));

// Fetch one page of the inbox. A page with an unreadable row still shows the rows that were read.
export const fetchWorkflows = createAsyncThunk<
  InboxWorkflowsPage,
  | string
  | {
      filter?: string;
      page?: number;
      limit?: number;
      sortOrder?: 'asc' | 'desc';
      /** Zone names; none or empty means every zone. */
      zones?: readonly string[];
    }
  | undefined,
  { rejectValue: string }
>('workflows/fetchWorkflows', async (payload, { rejectWithValue }) => {
  try {
    const filter = typeof payload === 'string' ? payload : payload?.filter;
    const page = typeof payload === 'object' ? payload?.page : undefined;
    const limit = typeof payload === 'object' ? payload?.limit : undefined;
    const sortOrder = typeof payload === 'object' ? payload?.sortOrder : undefined;
    const zones = typeof payload === 'object' ? payload?.zones : undefined;
    const answer = await getInboxWorkflows({
      filter: filter || 'all',
      page: page || 1,
      limit: limit || 25,
      sortOrder: sortOrder || 'desc',
      ...(zones && zones.length > 0 ? { zone: zones } : {}),
    });
    return acceptDegraded(answer);
  } catch (err) {
    return rejectWithValue(extractErrorMessage(err, 'Error fetching workflows'));
  }
});

// Respond to a workflow action with a chosen option
export const respondToWorkflowAction = createAsyncThunk<RespondedWorkflowAction | null, { workflow_action_id: number; chosen_id: string | number }>(
  'workflows/respondToWorkflowAction',
  async ({ workflow_action_id, chosen_id }, { rejectWithValue }) => {
    try {
      return receiptOf(await putWorkflowRespond(workflow_action_id, { chosen_id: String(chosen_id) }));
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Error responding to workflow action'));
    }
  }
);

// Save workflow action with filled response inputs
export const saveWorkflowAction = createAsyncThunk<UpdatedWorkflowAction | null, { workflow_action_id: number } & SaveWorkflowAction>(
  'workflows/saveWorkflowAction',
  async ({ workflow_action_id, response_inputs, dropdown_parameters }, { rejectWithValue }) => {
    try {
      return receiptOf(await putWorkflowAction(workflow_action_id, { response_inputs, dropdown_parameters }));
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Error saving workflow action'));
    }
  }
);

export const markWorkflowAsRead = createAsyncThunk<{ workflow_id: number }, number, { rejectValue: string }>(
  'workflows/markWorkflowAsRead',
  async (workflowId, { rejectWithValue }) => {
    try {
      // The receipt carries nothing the inbox keeps; only a failed call matters.
      await putWorkflowRead(workflowId);
      return { workflow_id: workflowId };
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Error marking workflow as read'));
    }
  }
);

// Workflows slice
const workflowsSlice = createSlice({
  name: 'workflows',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch workflows
      // A filter, sort or page change reads again while an earlier read may still be on its way. Only the latest read's
      // answer is kept: an earlier page that lands after it would fill the list under the wrong filter.
      .addCase(fetchWorkflows.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(fetchWorkflows.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        const arg = action.meta.arg;
        const requestedPage = typeof arg === 'object' && arg?.page ? arg.page : 1;

        if (requestedPage > 1) {
          const existingIds = new Set(state.workflows.map((workflow) => workflow.workflow_id));

          action.payload.workflows.forEach((workflow) => {
            if (!existingIds.has(workflow.workflow_id)) {
              state.workflows.push(toDraft(workflow));
            }
          });
        } else {
          state.workflows = toDraft([...action.payload.workflows]);
        }

        state.filterOptions = toDraft(action.payload.filterOptions);
        state.zoneOptions = toDraft(action.payload.zoneOptions);
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchWorkflows.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.error = extractErrorMessage(action.payload, 'Unknown error');
      })
      // Respond to workflow action
      .addCase(respondToWorkflowAction.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(respondToWorkflowAction.fulfilled, (state, action) => {
        state.updateLoading = false;
        if (action.payload === null) return;
        const { workflow_id, workflow_action_id } = action.payload;
        const workflowIndex = state.workflows.findIndex((workflow) => workflow.workflow_id === workflow_id);

        if (workflowIndex !== -1) {
          const workflow = state.workflows[workflowIndex];
          const actionIndex = workflow.workflows_actions.findIndex((act) => act.workflow_action_id === workflow_action_id);
          if (actionIndex !== -1) {
            // Update the workflow action with the chosen option
            workflow.workflows_actions[actionIndex] = toDraft(listActionOf(action.payload));
          }
        }
      })
      .addCase(respondToWorkflowAction.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = extractErrorMessage(action.payload, 'Unknown error');
      })

      // Save workflow action with response inputs
      .addCase(saveWorkflowAction.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(saveWorkflowAction.fulfilled, (state, action) => {
        state.updateLoading = false;
        if (action.payload === null) return;
        const { workflow_id, workflow_action_id } = action.payload;
        const workflowIndex = state.workflows.findIndex((workflow) => workflow.workflow_id === workflow_id);

        if (workflowIndex !== -1) {
          const workflow = state.workflows[workflowIndex];
          const actionIndex = workflow.workflows_actions.findIndex((act) => act.workflow_action_id === workflow_action_id);
          if (actionIndex !== -1) {
            // Update the workflow action with the saved response inputs
            workflow.workflows_actions[actionIndex] = toDraft(listActionOf(action.payload));
          }
        }
      })
      .addCase(saveWorkflowAction.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = extractErrorMessage(action.payload, 'Unknown error');
      });
  },
});

export default workflowsSlice.reducer;
